import type { ReactNode } from 'react';
import { usePermission, useHasRole, usePropertyPermission } from '../../hooks/usePermission';
import type { Permission } from '../../types/permissions';

interface PermissionGateProps {
  /** Required permission -- children are rendered only when the user holds it. */
  permission?: Permission;
  /** If set, the permission is checked against this specific property scope. */
  propertyId?: string;
  /** Required role -- children are rendered only when the user holds it. */
  role?: string;
  /** Optional fallback rendered when access is denied (defaults to nothing). */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Conditionally renders children based on the current user's permissions or role.
 *
 * If both `permission` and `role` are provided, the user must satisfy **both**.
 * If `propertyId` is provided alongside `permission`, checks property-scoped access.
 * If neither constraint is provided, children are always rendered.
 */
export function PermissionGate({
  permission,
  propertyId,
  role,
  fallback = null,
  children,
}: PermissionGateProps) {
  const hasGlobalPermission = usePermission(
    (permission ?? 'system:full_access') as Permission,
  );
  const hasPropertyPerm = usePropertyPermission(
    (permission ?? 'system:full_access') as Permission,
    propertyId,
  );
  const hasRoleResult = useHasRole(role ?? '');

  // When no constraints are specified, always render children
  const permissionOk = permission
    ? propertyId
      ? hasPropertyPerm
      : hasGlobalPermission
    : true;
  const roleOk = role ? hasRoleResult : true;

  if (permissionOk && roleOk) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}
